import { createStyles, Grid, makeStyles } from '@material-ui/core';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';
import {
  limitForMaxValue,
  limitsHaveRange,
  regularCoverages,
  specialCoverages,
} from 'data-layer-cr/dist/functions/coverages';
import {
  CoverageCatalog,
  Quotation,
  QuotationCoverage,
} from 'data-layer-cr/dist/models';
import React from 'react';

import {
  Body2Text,
  colors,
  DefaultButton,
  H4Text,
  HeaderText,
  moneyFormat,
} from 'design-system';
import { capitalize } from 'lodash';

const useStyles = makeStyles(() =>
  createStyles({
    alignCenter: {
      textAlign: 'left',
    },
    coverageName: {
      color: colors.abbey,
    },
    bodyText: {
      fontWeight: 400,
      lineHeight: '18px',
    },
    separator: {
      marginRight: 5,
      marginLeft: 5,
    },
    spaceCoverage: { marginBottom: 5 },
  }),
);

const { formatMoney } = moneyFormat;

// Single or ranged insured value
const renderLimits = (
  currentCoverage: CoverageCatalog,
  insuredValue: number,
  classes: ClassNameMap,
  quotation: Quotation,
): React.ReactNode => {
  // If a coverage have a range we should render it
  if (limitsHaveRange(currentCoverage)) {
    const limits = limitForMaxValue(currentCoverage, insuredValue);
    if (limits !== undefined) {
      return (
        <Grid container item direction="row" className={classes.alignCenter}>
          <Grid>
            <HeaderText
              data-testid={`regular-coverage-${currentCoverage.code}-minimum`}
            >
              {formatMoney(
                limits.minimum,
                false,
                quotation.quotationProfile.typeOfMoney,
              )}
            </HeaderText>
            <Body2Text
              className={classes.bodyText}
              data-testid={`regular-coverage-${currentCoverage.code}-minimum-text`}
            >
              por persona
            </Body2Text>
          </Grid>
          <HeaderText
            className={classes.separator}
            data-testid={`regular-coverage-${currentCoverage.code}-separator`}
          >
            {' '}
            /{' '}
          </HeaderText>
          <Grid>
            <HeaderText
              style={{ textAlign: 'right' }}
              data-testid={`regular-coverage-${currentCoverage.code}-maximum`}
            >
              {formatMoney(
                limits.maximum,
                false,
                quotation.quotationProfile.typeOfMoney,
              )}
            </HeaderText>
            <Body2Text
              className={classes.bodyText}
              style={{ textAlign: 'right' }}
              data-testid={`regular-coverage-${currentCoverage.code}-maximum-text`}
            >
              por accidente
            </Body2Text>
          </Grid>
        </Grid>
      );
    }
    return <HeaderText data-testid="regular-coverage-n/a">n / a</HeaderText>;
  }
  return (
    <HeaderText
      data-testid={`regular-coverage-${currentCoverage.code}-insuredValue`}
    >
      {formatMoney(insuredValue, false, quotation.quotationProfile.typeOfMoney)}
    </HeaderText>
  );
};

type Props = {
  productCoverages: QuotationCoverage[];
  catalogCoverages: CoverageCatalog[];
  isEditable: boolean;
  quotation: Quotation;
};

const QuotationCoverages = ({
  productCoverages,
  catalogCoverages,
  isEditable,
  quotation,
}: Props): JSX.Element => {
  const classes = useStyles();
  const specialCoverageList = specialCoverages(catalogCoverages);
  const regularCoverageList = regularCoverages(catalogCoverages);

  return (
    <Grid container spacing={2}>
      <Grid
        container
        item
        xs={12}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        {isEditable && (
          <Grid item>
            <DefaultButton
              data-testid="personalize-button"
              variant="outlined"
              fullWidth
            >
              Personalizar
            </DefaultButton>
          </Grid>
        )}
      </Grid>
      <Grid container item spacing={1} data-testid="list-coverage">
        {productCoverages.map((coverage) => {
          // This is used to check if a coverage has limits
          const currentCoverage = regularCoverageList.find(
            (cov) => cov.code === coverage.coverageId,
          );
          if (currentCoverage === undefined) {
            return null;
          }
          return (
            <Grid container item key={coverage.coverageId} xs={12} lg={6}>
              <Grid item xs={12}>
                <H4Text
                  className={classes.coverageName}
                  data-testid={`regular-coverage-${currentCoverage.code}-name`}
                >
                  {capitalize(currentCoverage.name)}
                </H4Text>
              </Grid>
              {currentCoverage.code !== '0058' && (
                <Grid item xs={12} className={classes.spaceCoverage}>
                  {renderLimits(
                    currentCoverage,
                    coverage.insuredValue,
                    classes,
                    quotation,
                  )}
                </Grid>
              )}
            </Grid>
          );
        })}

        {/** Special Coverages */}
        {specialCoverageList.map((specialCov) => (
          <Grid container item key={specialCov.code}>
            <Grid item xs={12}>
              <H4Text
                className={classes.coverageName}
                data-testid={`special-coverage-${specialCov.code}-label`}
              >
                {specialCov.label}
              </H4Text>
            </Grid>
            <Grid item xs={12}>
              <HeaderText
                data-testid={`special-coverage-${specialCov.code}-content`}
              >
                {specialCov.content}
              </HeaderText>
            </Grid>
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
};

export default QuotationCoverages;
